import type { TrackingContextValue } from '@ps/web-shared-analytics';
import { TrackingCategory, TrackingContext } from '@ps/web-shared-analytics';
import { kebabCase, merge } from 'lodash';
import React, { useContext, useRef } from 'react';

import ErrorBoundary from '@swf/components/ErrorBoundary/ErrorBoundary';
import VisibilityTracker from '@swf/components/VisibilityTracker/VisibilityTracker';
import DisplayConfigProvider from '@swf/sports/providers/DisplayConfigProvider';

import type { DateTimeOptions } from '../FormattedDateTime/FormattedDateTime';

interface Props {
  name: `Sports ${string} Component` | `Sports ${string} Widget`;
  testId?: `sports-${string}-component` | `isp-sports-widget-${string}`;
  dateTimeOptions?: DateTimeOptions;
  widgetContext?: string;
  asChild?: boolean;
}

export const SportsComponent: React.FC<React.PropsWithChildren<Props>> = ({
  name,
  testId,
  children,
  dateTimeOptions,
  widgetContext,
  asChild
}) => {
  const derivedTestId = testId ?? kebabCase(name);
  const trackingContextValue = useContext(TrackingContext);

  const { current: trackingContext } = useRef<TrackingContextValue>({
    category: TrackingCategory.Sports,
    widget: { widgetName: name, widgetId: derivedTestId, widgetContext }
  });

  return (
    <TrackingContext.Provider value={trackingContext}>
      <DisplayConfigProvider dateTimeOptions={dateTimeOptions}>
        <VisibilityTracker
          name={name}
          trackingContext={merge({}, trackingContextValue, trackingContext)}
        >
          {asChild ? (
            children
          ) : (
            <ErrorBoundary
              beforeCapture={(scope) => {
                scope.setTag('widget.id', derivedTestId);
              }}
            >
              <div data-testid={derivedTestId}>{children}</div>
            </ErrorBoundary>
          )}
        </VisibilityTracker>
      </DisplayConfigProvider>
    </TrackingContext.Provider>
  );
};
